import * as THREE from 'three'
import { useGLTF } from '@react-three/drei'
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js'
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js'
import type { GLTFLoader } from 'three-stdlib'
import type { ProductConfig } from './types'

/**
 * De laadketen voor productmodellen.
 *
 * Drie decoders, alle drie uit een lokaal pad in plaats van een CDN. Dat is geen
 * voorkeur maar een eis: de kiosk moet doordraaien als de beurs-wifi wegvalt
 * (PLAN.md §4, offline-first). De bestanden staan in /public/draco en
 * /public/basis en komen uit het three-pakket, zodat ze bij de versie passen.
 *
 * - **Draco** comprimeert de geometrie. Op een machinemodel met veel kleine
 *   onderdelen scheelt dat gerust een factor vijf.
 * - **Meshopt** doet hetzelfde met een andere afweging: iets minder compressie,
 *   veel snellere decode. Beide zijn aan, want welke van de twee je krijgt hangt
 *   af van de exporteur.
 * - **KTX2/Basis** houdt texturen gecomprimeerd tot in het videogeheugen. Een
 *   PNG van 2048² kost 16 MB VRAM, dezelfde textuur als KTX2 ongeveer 2,7 MB.
 *   Op een mini-pc met gedeeld geheugen is dat het verschil tussen soepel en
 *   niet.
 */

const DRACO_PAD = '/draco/'
const BASIS_PAD = '/basis/'

let ktx2: KTX2Loader | null = null

/**
 * De KTX2-loader heeft de renderer nodig om te bepalen welk compressieformaat
 * de GPU aankan (ASTC, ETC2, BC7, ...). Daarom kan hij pas bestaan als er een
 * WebGL-context is, en houden we er één aan per pagina.
 */
export function ktx2Loader(renderer: THREE.WebGLRenderer): KTX2Loader {
  if (!ktx2) {
    ktx2 = new KTX2Loader().setTranscoderPath(BASIS_PAD)
  }
  ktx2.detectSupport(renderer)
  return ktx2
}

/** Hangt KTX2 en Meshopt aan de GLTFLoader die drei voor ons opzet. */
export const maakLoaderUitbreiding =
  (renderer: THREE.WebGLRenderer) => (loader: GLTFLoader) => {
    loader.setKTX2Loader(ktx2Loader(renderer) as never)
    loader.setMeshoptDecoder(MeshoptDecoder)
  }

/**
 * Laadt het model van een product alvast. Aanroepen als de bezoeker nog naar
 * iets anders kijkt: dan staat het volgende product er al als hij doorklikt.
 * Stil falen is hier juist: een mislukte prefetch mag niets kapotmaken, de
 * echte laadpoging meldt de fout wel.
 */
export function laadAlvast(product: ProductConfig | undefined) {
  if (!product?.modelUrl) return
  try {
    useGLTF.preload(product.modelUrl, DRACO_PAD, true)
  } catch {
    /* stil: dit is een optimalisatie, geen functionaliteit */
  }
}

/** Het volgende product in de catalogus, om vooruit te laden. */
export function volgende(lijst: ProductConfig[], id: string): ProductConfig | undefined {
  const i = lijst.findIndex((p) => p.id === id)
  return i < 0 ? undefined : lijst[(i + 1) % lijst.length]
}

export { DRACO_PAD }
